'use client';

import { Credits } from './Credits';
import { NotificationToast } from './NotificationToast';
import { FaGift } from '@/components/icons';
import { AchievementIcon } from '@/screens/AchievementsScreen/components/AchievementIcon';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import translations from './AchievementUnlockToast.translations';

interface Props {
  /** Catalog icon file name, e.g. `relic.png` (served from /public/achievements). */
  iconFile: string;
  /** Localised achievement title. */
  title: string;
  /** Full reward coins of the achievement. */
  rewardCredits?: number;
  /** Full reward XP of the achievement. */
  rewardXp?: number;
  /** Whether the bonus was credited. false → reward locked behind deposits; show a claim hint. */
  claimed?: boolean;
  /** When set, the whole toast is a button (tap → open the achievement / claim sheet). */
  onClick?: () => void;
}

/**
 * In-app notification shown when a user unlocks an achievement live. Rendered inside a sonner
 * `toast.custom(...)` from `useUserNotificationsSubscription`. Achievement dressing over the
 * shared `NotificationToast` surface. Tappable → trophy sheet.
 */
export function AchievementUnlockToast({
  iconFile,
  title,
  rewardCredits,
  rewardXp,
  claimed = true,
  onClick,
}: Props) {
  const { t } = useLocalTranslation(translations);
  const hasReward = !!rewardCredits || !!rewardXp;

  // Icon tile: blurred backdrop + sharp art (same technique as the list cards).
  const icon = (
    <>
      <div className="pointer-events-none absolute inset-0 scale-[1.5] opacity-40 blur-[14px]">
        <AchievementIcon iconFile={iconFile} alt="" fill className="object-contain" />
      </div>
      <div className="animate-shot-ach-icon-pop relative h-full w-full">
        <AchievementIcon iconFile={iconFile} alt={title} fill className="object-contain" />
      </div>
    </>
  );

  let reward = null;
  if (hasReward && claimed) {
    reward = (
      <>
        {!!rewardCredits && (
          <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-400">
            +<Credits amount={rewardCredits} iconSize={11} />
          </span>
        )}
        {!!rewardXp && <span className="text-xs font-semibold text-label-secondary">+{rewardXp} {t('xp')}</span>}
      </>
    );
  } else if (hasReward) {
    // Unlocked but reward locked behind deposits — invite to claim.
    reward = (
      <span className="flex items-center gap-1 text-xs font-semibold text-amber-400">
        <FaGift size={10} />
        {!!rewardCredits && (
          <span className="inline-flex items-center gap-0.5">
            +<Credits amount={rewardCredits} iconSize={10} />
          </span>
        )}
        <span className="text-amber-400/90">· {t('claimReward')} →</span>
      </span>
    );
  }

  return (
    <NotificationToast icon={icon} overline={t('unlocked')} title={title} reward={reward} accent="amber" onClick={onClick} />
  );
}
